import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from '@/components/cms/ui/dropdown-menu';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { IconButton } from '@/components/ui/icon-button';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import LureoButton from '@/common/LureoButton';
import { DesktopOnlyHint } from '@/components/handoff/DesktopOnlyHint';
import { isMobileDevice } from '@/utils/deviceDetection';
import {
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  ClockCounterClockwiseIcon,
  DotsThreeVerticalIcon,
  FileDashedIcon,
  GearIcon,
  LinkIcon,
  LockIcon,
  ShareIcon,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { StoryVersion } from '@/hooks/useStoryEditData';
import type { ISubscriptionPlanService } from '@/modules/subscriptionPlan/types';
import { CardHeading } from '@/components/ui/card';

interface StoryVersionHistoryProps {
  versions: StoryVersion[];
  loadingVersions: boolean;
  subscriptionPlan: string | undefined;
  subscriptionPlanService: ISubscriptionPlanService;
  onUnpublishVersion: (version: StoryVersion) => void;
  onArchiveVersion: (version: StoryVersion) => void;
  onMakeEditableCopy: (version: StoryVersion) => void;
  onPublishDraft: (sceneInstanceId: string, versionNumber?: number) => void;
  onAdminStatusChange: (newStatus: 'draft' | 'live' | 'pending' | 'archived' | 'template', sceneInstanceId?: string) => void;
  onArchiveDraftVersion: (version: StoryVersion) => void;
  onOpenInEditor: (sceneInstanceId?: string) => void;
  /** Copies the version's share link: the draft link for a draft, the story's permanent link for the live version. */
  onCopyLink?: (version: StoryVersion) => void;
  /** Revokes the version's current link and mints a replacement; every copy of the old one stops working. */
  onSwapLink?: (version: StoryVersion) => void;
}

export function StoryVersionHistory({
  versions,
  loadingVersions,
  subscriptionPlan,
  subscriptionPlanService,
  onUnpublishVersion,
  onArchiveVersion,
  onMakeEditableCopy,
  onPublishDraft,
  onAdminStatusChange,
  onArchiveDraftVersion,
  onOpenInEditor,
  onCopyLink,
  onSwapLink,
}: StoryVersionHistoryProps) {
  // A live link is revoked, not swapped (the destinations card); only a preview link swaps
  const linkItems = (version: StoryVersion) =>
    onCopyLink && onSwapLink ? (
      <>
        <DropdownMenuItem onClick={() => onCopyLink(version)}>
          <LinkIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
          Copy link
        </DropdownMenuItem>
        {version.status !== 'live' && (
          <DropdownMenuItem onClick={() => onSwapLink(version)}>
            <ArrowsClockwiseIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
            Swap link
          </DropdownMenuItem>
        )}
      </>
    ) : null;
  return (
    <Card>
      <CardHeading as="h2" icon={<ClockCounterClockwiseIcon />} title="Version History" />

      <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
        {versions.length === 0 ? (
          <EmptyState>No versions found</EmptyState>
        ) : (
          <>
            {versions.map((version) => (
              <Card key={version.id}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-lureo-18 font-semibold tabular-nums text-lureo-ink leading-7">{`v${version.versionNumber}`}</span>
                    <StatusBadge status={version.status} />
                  </div>

                  <div className="flex items-center space-x-2">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild disabled={version.status === 'locked'}>
                        <IconButton label="Version options" tone="outline" disabled={version.status === 'locked'}>
                          <DotsThreeVerticalIcon className="h-4 w-4" />
                        </IconButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        avoidCollisions={true}
                        onCloseAutoFocus={(e) => e.preventDefault()}
                      >
                        {version.status === 'live' && (
                          <>
                            {linkItems(version)}
                            <DropdownMenuItem onClick={() => onUnpublishVersion(version)}>
                              <FileDashedIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Unpublish
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onArchiveVersion(version)}>
                              <LockIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onMakeEditableCopy(version)}>
                              <ClockCounterClockwiseIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Edit a Copy
                            </DropdownMenuItem>
                          </>
                        )}

                        {version.status === 'pending' && (
                          <>
                            {linkItems(version)}
                            <DropdownMenuItem onClick={() => onUnpublishVersion(version)}>
                              <FileDashedIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Unpublish
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onMakeEditableCopy(version)}>
                              <ClockCounterClockwiseIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Edit a Copy
                            </DropdownMenuItem>
                          </>
                        )}

                        {(version.status === 'draft' || version.status === 'template') && (
                          <>
                            {version.status === 'draft' && linkItems(version)}
                            <DropdownMenuItem onClick={() => onPublishDraft(version.id, version.versionNumber)}>
                              <ShareIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Publish
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onArchiveDraftVersion(version)}>
                              <LockIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onMakeEditableCopy(version)}>
                              <ClockCounterClockwiseIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Edit a Copy
                            </DropdownMenuItem>
                          </>
                        )}

                        {version.status === 'archived' && (
                          <>
                            <DropdownMenuItem onClick={() => onMakeEditableCopy(version)}>
                              <ClockCounterClockwiseIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                              Edit a Copy
                            </DropdownMenuItem>
                          </>
                        )}

                        {subscriptionPlanService.canEditTemplateMetadata(subscriptionPlan || '') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <GearIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
                                Change Status
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                <DropdownMenuItem onClick={() => onAdminStatusChange('draft', version.id)}>
                                  Draft
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAdminStatusChange('live', version.id)}>
                                  Live
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAdminStatusChange('pending', version.id)}>
                                  Pending
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAdminStatusChange('archived', version.id)}>
                                  Archived
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onAdminStatusChange('template', version.id)}>
                                  Template
                                </DropdownMenuItem>
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-lureo-14 text-lureo-ink-subtle sm:text-lureo-12 leading-5 sm:leading-4">
                    Last updated {formatDistanceToNow(version.publishedAt, { addSuffix: true })}
                  </p>

                  {version.status === 'draft' && (
                    <div className="flex flex-col items-stretch gap-1 sm:items-end">
                      <LureoButton
                        variant="secondary"
                        disabled={isMobileDevice()}
                        className="h-8 w-full rounded-lureo-radius-button-pill px-3 min-h-[44px] sm:min-h-0 sm:w-auto"
                        onClick={() => onOpenInEditor(version.id)}
                      >
                        Open
                        <ArrowRightIcon className="w-4 h-4" />
                      </LureoButton>
                      {isMobileDevice() && <DesktopOnlyHint className="block w-full text-center" />}
                    </div>
                  )}
                </div>
              </Card>
            ))}

            {loadingVersions && (
              <div className="flex items-center justify-center py-2">
                <LoadingSpinner size="sm" className="mr-2" />
                <span className="text-lureo-12 text-lureo-ink-subtle leading-4">Loading version history...</span>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
